import { useState, useEffect } from 'react';
import { Space } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import CustomTable from '../../../components/CustomTable';
import CustomModal from '../../../components/CustomModal';
import CustomInput from '../../../components/CustomInput';
import { toast } from 'react-toastify';
import {
    allApiKeys,
    apiKeysLoadingStatus,
    createApiKey,
    deleteApiKey,
    getApiKeys,
    updateApiKey,
} from '../../../redux/slices/settingsSlice';

export default function ApiKeysPage() {
    const dispatch = useDispatch();

    const apiKeysStatus = useSelector(apiKeysLoadingStatus);
    const apiKeys = useSelector(allApiKeys);

    const [data, setData] = useState({
        client: '',
        api_key: '',
        api_secret: '',
    });
    const [isCreateApiKeyModalVisible, setIsCreateApiKeyModalVisible] =
        useState(false);
    const [isEditApiKeyModalVisible, setIsEditApiKeyModalVisible] =
        useState(false);
    const [isDeleteApiKeyModalVisible, setIsDeleteApiKeyModalVisible] =
        useState(false);
    const [selectedApiKey, setSelectedApiKey] = useState({
        id: null,
        client: '',
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getApiKeys());
    }, [dispatch]);

    const toggleCreateApiKeyModal = () => {
        resetState();
        setErrors({});
        setIsCreateApiKeyModalVisible(!isCreateApiKeyModalVisible);
    };

    const toggleEditApiKeyModal = (record) => {
        if (!record) {
            setIsEditApiKeyModalVisible(!isEditApiKeyModalVisible);
        } else {
            setData({
                client: record.client,
                api_key: record.api_key,
                api_secret: record.api_secret,
            });
            setSelectedApiKey({ id: record.id, client: record.client });
            setErrors({});
            setIsEditApiKeyModalVisible(!isEditApiKeyModalVisible);
        }
    };

    const toggleDeleteApiKeyModal = (record) => {
        if (!record) {
            setIsDeleteApiKeyModalVisible(!isDeleteApiKeyModalVisible);
        } else {
            setSelectedApiKey({ id: record.id, client: record.client });
            setIsDeleteApiKeyModalVisible(!isDeleteApiKeyModalVisible);
        }
    };

    const resetState = () => {
        setData({
            client: '',
            api_key: '',
            api_secret: '',
        });
    };

    const onInputChange = (e) => {
        const { id, value } = e.target;

        setData({
            ...data,
            [id]: value,
        });
    };

    const onCreateApiKey = async () => {
        try {
            await dispatch(createApiKey(data)).unwrap();
            toast.success('New API key successfully added');
            toggleCreateApiKeyModal();
            dispatch(getApiKeys());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onEditApiKey = async () => {
        try {
            await dispatch(
                updateApiKey({ id: selectedApiKey.id, body: data }),
            ).unwrap();
            toast.success('Changes saved successfully');
            toggleEditApiKeyModal();
            dispatch(getApiKeys());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onDeleteApiKey = async () => {
        try {
            await dispatch(deleteApiKey(selectedApiKey.id)).unwrap();
            toast.success('API key successfully deleted');
            toggleDeleteApiKeyModal();
            dispatch(getApiKeys());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const actionsOption = {
        title: 'Actions',
        render: (_, record) => (
            <Space size="middle">
                <EditOutlined onClick={() => toggleEditApiKeyModal(record)} />
                <DeleteOutlined
                    onClick={() => toggleDeleteApiKeyModal(record)}
                />
            </Space>
        ),
    };

    const getFilteredApiKeys = (data, filter) =>
        data.filter((el) =>
            el?.client.toLowerCase().includes(filter.toLowerCase()),
        );

    return (
        <>
            <Helmet>
                <title>Console Defentry - Settings - API Keys</title>
            </Helmet>
            <div className="table-wrapper">
                <CustomTable
                    tableHeader={['Client', 'API Key', 'API Secret', 'Actions']}
                    functionalCell={{ Actions: actionsOption }}
                    onAddButtonClick={toggleCreateApiKeyModal}
                    tablePannelTitle="List of API keys"
                    searchInputPlaceholder="Search by name"
                    topPannelButtonText="Add API key"
                    data={apiKeys}
                    getFilteredData={getFilteredApiKeys}
                    isLoading={apiKeysStatus === 'loading'}
                    withFilter
                />
            </div>
            <CustomModal
                title="Create API key"
                subText="Please fill the fields below"
                subTextAlign="left"
                isModalVisible={isCreateApiKeyModalVisible}
                onAcceptButtonClick={onCreateApiKey}
                onCancelButtonClick={toggleCreateApiKeyModal}
                cancelButtonText="Cancel"
                acceptButtonText="Create"
            >
                <div className="form__item-wrapper">
                    <CustomInput
                        id="client"
                        placeholder="Enter name"
                        onInputChange={onInputChange}
                        inputValue={data.client}
                        labelText="Name"
                        width="100%"
                        errorText={errors?.client}
                    />
                </div>
            </CustomModal>
            <CustomModal
                title="Edit API key"
                subText="Please fill the fields below"
                subTextAlign="left"
                isModalVisible={isEditApiKeyModalVisible}
                onAcceptButtonClick={onEditApiKey}
                onCancelButtonClick={toggleEditApiKeyModal}
                cancelButtonText="Cancel"
                acceptButtonText="Update"
            >
                <div className="form__item-wrapper">
                    <CustomInput
                        id="client"
                        placeholder="Enter name"
                        onInputChange={onInputChange}
                        inputValue={data.client}
                        labelText="Name"
                        width="100%"
                        errorText={errors?.client}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="api_key"
                        placeholder="Enter API key"
                        onInputChange={onInputChange}
                        inputValue={data.api_key}
                        labelText="API key"
                        width="100%"
                        errorText={errors?.api_key}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="api_secret"
                        placeholder="Enter API secret"
                        onInputChange={onInputChange}
                        inputValue={data.api_secret}
                        labelText="API secret"
                        width="100%"
                        errorText={errors?.api_secret}
                    />
                </div>
            </CustomModal>
            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${selectedApiKey.client} `}</span>
                        API key?
                    </p>
                }
                subText="You will delete this API key without recovery"
                subTextAlign="center"
                isModalVisible={isDeleteApiKeyModalVisible}
                onAcceptButtonClick={onDeleteApiKey}
                onCancelButtonClick={toggleDeleteApiKeyModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            ></CustomModal>
        </>
    );
}
